/**
 * 위키 본문 마크다운 렌더러.
 *
 * - 첫 단락이 `> ...` callout 이면 별도 TakeawayCallout 컴포넌트로 분리 렌더 (강조).
 * - 본문 내 `[[slug]]` 또는 `[[slug|표시 텍스트]]` 위키링크를 <Link to="/wikis/$slug"> 로 변환.
 * - 그 외는 chat 의 Markdown 컴포넌트와 동일한 토큰 매핑 사용.
 */
import { InlineMermaid } from '@/components/chat/InlineMermaid';
import { cn } from '@/lib/cn';
import { alignClass } from '@/lib/markdown-align';
import { normalizeCjkEmphasis } from '@/lib/markdown-cjk-emphasis';
import { isInlineRenderCodeChild, isMermaidLang } from '@/lib/mermaid-fence';
import ReactMarkdown, { type Components } from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import { MarkdownImg } from './markdownImg';
import { transformWikilinks } from './wikilink-utils';

/**
 * PPTX 등 변환기가 `<p style=...>` `<span style=...>` 같은 raw HTML 을 마크다운에 섞어 넣어도
 * 안전하게 렌더하기 위한 sanitize schema. defaultSchema 가:
 *  - `<script>` `<style>` `<iframe>` 등 위험 태그 차단
 *  - `style` `class` `onclick` 등 시각·이벤트 속성 제거 (그래서 시끄러운 폰트/색깔이 자동 사라짐)
 *  - `<img src>` 는 http(s)/상대경로만 허용 (data:javascript: 차단)
 */
const sanitizeSchema = defaultSchema;
const REHYPE_PLUGINS = [rehypeRaw, [rehypeSanitize, sanitizeSchema]] as never;

interface Props {
  takeaway?: string | null;
  body: string;
  className?: string;
  /** outlinks 정보가 있으면 끊긴 링크를 빨갛게 표시. 없으면 모두 정상 링크 처리. */
  resolvedSlugs?: Set<string>;
  /** [[slug]] 표시 텍스트를 슬러그 대신 페이지 제목으로 치환. */
  slugToTitle?: Map<string, string>;
  /** [[slug]] Link 에 추가할 query string (예: 'from=chat'). */
  linkSearchParam?: string;
}

function buildComponents(
  resolved: Set<string> | undefined,
  slugToTitle: Map<string, string> | undefined,
  linkSearchParam: string | undefined,
): Components {
  const xform = (children: import('react').ReactNode) =>
    transformWikilinks(children, { resolved, slugToTitle, linkSearchParam });
  return {
    p: ({ children }) => <p className="my-3 first:mt-0 last:mb-0 leading-7">{xform(children)}</p>,
    h1: ({ children }) => (
      <h1 className="mt-6 mb-3 text-2xl font-semibold tracking-tight first:mt-0">
        {xform(children)}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 className="mt-6 mb-3 text-xl font-semibold tracking-tight first:mt-0">
        {xform(children)}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="mt-5 mb-2 text-lg font-semibold tracking-tight first:mt-0">
        {xform(children)}
      </h3>
    ),
    h4: ({ children }) => (
      <h4 className="mt-4 mb-2 text-base font-semibold first:mt-0">{xform(children)}</h4>
    ),
    // strong/em/del 안의 [[slug]] 도 위키링크로 처리 — `**[[slug]]**` 같은 흔한 패턴 대응.
    strong: ({ children }) => (
      <strong className="font-semibold text-foreground">{xform(children)}</strong>
    ),
    em: ({ children }) => <em className="italic">{xform(children)}</em>,
    del: ({ children }) => <del>{xform(children)}</del>,
    ul: ({ children }) => <ul className="my-3 ml-5 list-disc space-y-1.5">{children}</ul>,
    ol: ({ children }) => <ol className="my-3 ml-5 list-decimal space-y-1.5">{children}</ol>,
    li: ({ children }) => <li className="leading-7 [&>p]:my-0">{xform(children)}</li>,
    blockquote: ({ children }) => (
      <blockquote className="my-3 border-l-2 border-border pl-4 text-foreground-muted">
        {children}
      </blockquote>
    ),
    hr: () => <hr className="my-5 border-border-muted" />,
    a: ({ href, children }) => (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="text-accent underline underline-offset-2 hover:opacity-80"
      >
        {children}
      </a>
    ),
    code: ({ className, children, ...rest }) => {
      const isBlock = /language-/.test(className ?? '');
      if (isBlock) {
        if (isMermaidLang(className)) {
          return <InlineMermaid raw={String(children).replace(/\n$/, '')} />;
        }
        return (
          <code className={cn('font-mono text-[13px]', className)} {...rest}>
            {children}
          </code>
        );
      }
      return (
        <code className="rounded-sm bg-code-bg px-1.5 py-0.5 font-mono text-[0.9em]" {...rest}>
          {children}
        </code>
      );
    },
    pre: ({ children }) => {
      if (isInlineRenderCodeChild(children)) {
        return <>{children}</>;
      }
      return (
        <pre className="my-3 overflow-x-auto rounded-md bg-code-bg p-3 font-mono text-[13px] leading-6">
          {children}
        </pre>
      );
    },
    table: ({ children }) => (
      <div className="my-3 overflow-x-auto">
        <table className="w-full border-collapse text-sm">{children}</table>
      </div>
    ),
    thead: ({ children }) => <thead className="border-b border-border">{children}</thead>,
    tr: ({ children }) => (
      <tr className="border-b border-border-muted last:border-0">{children}</tr>
    ),
    th: ({ children, node }) => (
      <th className={cn('px-3 py-2 font-medium text-foreground-muted', alignClass(node))}>
        {children}
      </th>
    ),
    td: ({ children, node }) => (
      <td className={cn('px-3 py-2 align-top', alignClass(node))}>{children}</td>
    ),
    img: ({ src, alt }) => (
      <MarkdownImg src={typeof src === 'string' ? src : undefined} alt={alt} />
    ),
  };
}

export function TakeawayCallout({ children }: { children: import('react').ReactNode }) {
  return (
    <aside
      className="rounded-md border-l-4 border-accent bg-accent-soft px-4 py-3 text-foreground"
      aria-label="핵심 takeaway"
    >
      <div className="mb-1 text-xs font-medium text-foreground-muted">핵심 takeaway</div>
      <div className="text-base leading-7">{children}</div>
    </aside>
  );
}

export function WikiMarkdown({
  takeaway,
  body,
  className,
  resolvedSlugs,
  slugToTitle,
  linkSearchParam,
}: Props) {
  const components = buildComponents(resolvedSlugs, slugToTitle, linkSearchParam);
  const rendered = normalizeCjkEmphasis(body);
  // takeaway 도 body 와 동일한 [[slug]] 처리 — broken/restricted 슬러그가 raw 로 노출되던 버그 fix.
  const takeawayNodes = takeaway
    ? transformWikilinks(takeaway, {
        resolved: resolvedSlugs,
        slugToTitle,
        linkSearchParam,
      })
    : null;
  return (
    <div className={cn('text-base space-y-4', className)}>
      {takeawayNodes ? <TakeawayCallout>{takeawayNodes}</TakeawayCallout> : null}
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={REHYPE_PLUGINS}
        components={components}
      >
        {rendered}
      </ReactMarkdown>
    </div>
  );
}
